import { DataTable } from "@/components/DataTable";
import { EditSheet } from "@/components/EditSheet";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, type UserRow } from "@/lib/api";
import { formatDate, relativeIso } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { ShieldBan, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const columns: ColumnDef<UserRow>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ getValue }) => {
      const name = getValue() as string;
      return name ? (
        name
      ) : (
        <span className="text-muted-foreground italic">No name</span>
      );
    },
  },
  { accessorKey: "email", header: "Email" },
  {
    accessorKey: "role",
    header: "Role",
    cell: ({ getValue }) => (
      <Badge variant="secondary">{String(getValue() ?? "user")}</Badge>
    ),
  },
  {
    accessorKey: "emailVerified",
    header: "Verified",
    cell: ({ getValue }) =>
      getValue() ? (
        <Badge variant="outline">Verified</Badge>
      ) : (
        <Badge variant="destructive">Unverified</Badge>
      ),
  },
  {
    accessorKey: "banned",
    header: "Status",
    cell: ({ getValue }) =>
      getValue() ? (
        <Badge variant="destructive">Banned</Badge>
      ) : (
        <Badge variant="outline">Active</Badge>
      ),
  },
  {
    accessorKey: "lastSignIn",
    header: "Last Sign-In",
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{relativeIso(getValue() as string | null)}</span>
    ),
  },
  {
    accessorKey: "createdAt",
    header: "Joined",
    cell: ({ getValue }) => formatDate(getValue() as string),
  },
];

const FIELDS = [
  { key: "id", label: "ID", readOnly: true },
  { key: "email", label: "Email", readOnly: true },
  { key: "name", label: "Name" },
  { key: "role", label: "Role" },
];

export function AdminUsersPage() {
  useDocumentTitle("Admin: Users");
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<UserRow | null>(null);

  const { data: listResponse, isLoading, isError, error } = useQuery({
    queryKey: ["admin-users"],
    queryFn: api.admin.users,
  });
  const data = listResponse?.data;
  const permissions = listResponse?._permissions;

  const mutation = useMutation({
    mutationFn: (data: Record<string, string>) =>
      api.admin.updateUser(selected!.id, { name: data.name, role: data.role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setSelected(null);
    },
  });

  const banMutation = useMutation({
    mutationFn: ({ id, banned }: { id: string; banned: boolean }) =>
      api.admin.updateUser(id, { banned }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setSelected(null);
    },
  });

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Users" subtitle="All users across your platform." />

      {isLoading ? (
        <LoadingIndicator />
      ) : isError || !data ? (
        <ErrorComponent error={error ?? "Failed to load users"} />
      ) : (
        <DataTable
          columns={columns}
          data={data}
          isLoading={false}
          onRowClick={permissions?.canEditName !== false ? setSelected : undefined}
        />
      )}

      <EditSheet
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Edit User"
        item={selected as Record<string, unknown> | null}
        fields={FIELDS}
        onSave={mutation.mutateAsync}
        isSaving={mutation.isPending}
        extraActions={
          selected && selected.role !== "super_admin" ? (
            selected.banned ? (
              <Button
                variant="outline"
                className="w-full"
                disabled={banMutation.isPending}
                onClick={() => banMutation.mutate({ id: selected.id, banned: false })}
              >
                <ShieldCheck className="size-4 mr-2" />
                {banMutation.isPending ? "Unbanning…" : "Unban user"}
              </Button>
            ) : (
              <Button
                variant="destructive"
                className="w-full"
                disabled={banMutation.isPending}
                onClick={() => banMutation.mutate({ id: selected.id, banned: true })}
              >
                <ShieldBan className="size-4 mr-2" />
                {banMutation.isPending ? "Banning…" : "Ban user"}
              </Button>
            )
          ) : null
        }
      />
    </div>
  );
}
